// src/client/pages/conversations.tsx
// Conversation Tracking — Übersicht und Verwaltung
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';
import { Input } from '@client/components/ui/input';
import { Badge } from '@client/components/ui/badge';
import { Label } from '@client/components/ui/label';
import { useToast } from '@client/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@client/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@client/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@client/components/ui/select';
import {
  MessageSquare,
  ExternalLink,
  User,
  Clock,
  AlertTriangle,
  Plus,
  Filter,
  Search,
} from 'lucide-react';
import { ApiClient } from '@client/lib/api-client';
import { queryKeys, invalidationHelpers } from '@client/lib/query-keys';
import type {
  Conversation,
  ConversationWithLog,
  ConversationStatus,
  ConversationFilters,
} from '@shared/types/core/conversation.types';

// ============================================================================
// Konstanten
// ============================================================================

const STATUS_LABELS: Record<ConversationStatus, string> = {
  'open': 'Offen',
  'outreach': 'Erstanfrage',
  'awaiting': 'Warte auf Antwort',
  'follow-up': 'Nachfassen',
  'replied': 'Antwort erhalten',
  'active': 'Laufender Dialog',
  'closed': 'Abgeschlossen'
};

const STATUS_COLORS: Record<ConversationStatus, string> = {
  'open': 'bg-slate-100 text-slate-700',
  'outreach': 'bg-blue-100 text-blue-700',
  'awaiting': 'bg-yellow-100 text-yellow-700',
  'follow-up': 'bg-orange-100 text-orange-700',
  'replied': 'bg-green-100 text-green-700',
  'active': 'bg-emerald-100 text-emerald-700',
  'closed': 'bg-gray-100 text-gray-500'
};

// ============================================================================
// Conversations Page
// ============================================================================

export default function ConversationsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Filter State
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [contextFilter, setContextFilter] = useState<string>('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog State
  const [selectedConversation, setSelectedConversation] = useState<ConversationWithLog | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<ConversationStatus>('open');
  const [statusNote, setStatusNote] = useState('');
  const [statusFollowUp, setStatusFollowUp] = useState('');
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [logAction, setLogAction] = useState<ConversationStatus>('active');
  const [logNote, setLogNote] = useState('');

  // ==================== QUERIES ====================

  const conversationsQuery = useQuery({
    queryKey: [...queryKeys.conversations.lists(), statusFilter, contextFilter, overdueOnly, searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (contextFilter !== 'all') params.set('context', contextFilter);
      if (overdueOnly) params.set('overdue', 'true');
      if (searchTerm) params.set('search', searchTerm);
      params.set('limit', '100');

      return ApiClient.get<{ conversations: Conversation[]; total: number }>(
        `/conversations?${params.toString()}`
      );
    },
  });

  const contextsQuery = useQuery({
    queryKey: [...queryKeys.conversations.all, 'contexts'],
    queryFn: () => ApiClient.get<{ contexts: string[] }>('/conversations/contexts'),
  });

  const overdueQuery = useQuery({
    queryKey: [...queryKeys.conversations.all, 'overdue-count'],
    queryFn: () => ApiClient.get<{ count: number }>('/conversations/overdue'),
  });

  // ==================== MUTATIONS ====================

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, note, followUpDays }: {
      id: number; status: ConversationStatus; note?: string; followUpDays?: number | null;
    }) => {
      return ApiClient.put(`/conversations/${id}/status`, { status, note, followUpDays });
    },
    onSuccess: () => {
      invalidationHelpers.invalidateConversations(queryClient);
      toast({ title: 'Status aktualisiert' });
      setStatusDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    },
  });

  const addLogMutation = useMutation({
    mutationFn: async ({ id, action, note }: { id: number; action: ConversationStatus; note?: string }) => {
      return ApiClient.post(`/conversations/${id}/log`, { action, note });
    },
    onSuccess: () => {
      invalidationHelpers.invalidateConversations(queryClient);
      toast({ title: 'Log-Eintrag hinzugefügt' });
      setLogDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    },
  });

  // ==================== HANDLERS ====================

  const openDetailDialog = async (conv: Conversation) => {
    try {
      const result = await ApiClient.get<{ success: boolean; data: ConversationWithLog }>(
        `/conversations/${conv.id}`
      );
      if (result.success && result.data) {
        setSelectedConversation(result.data);
        setDetailDialogOpen(true);
      }
    } catch (error) {
      toast({ title: 'Fehler beim Laden', variant: 'destructive' });
    }
  };

  const openStatusDialog = (conv: Conversation) => {
    setSelectedConversation(conv as ConversationWithLog);
    setNewStatus(conv.currentStatus);
    setStatusNote('');
    setStatusFollowUp('');
    setStatusDialogOpen(true);
  };

  const openLogDialog = (conv: Conversation) => {
    setSelectedConversation(conv as ConversationWithLog);
    setLogAction('active');
    setLogNote('');
    setLogDialogOpen(true);
  };

  const handleStatusUpdate = () => {
    if (!selectedConversation) return;
    updateStatusMutation.mutate({
      id: selectedConversation.id,
      status: newStatus,
      note: statusNote || undefined,
      followUpDays: statusFollowUp ? parseInt(statusFollowUp) : null,
    });
  };

  const handleAddLog = () => {
    if (!selectedConversation) return;
    addLogMutation.mutate({
      id: selectedConversation.id,
      action: logAction,
      note: logNote || undefined,
    });
  };

  // ==================== HELPERS ====================

  const isOverdue = (conv: Conversation): boolean => {
    if (!conv.nextFollowUp) return false;
    return new Date(conv.nextFollowUp) <= new Date();
  };

  const formatDate = (date: Date | string | null): string => {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const conversations = conversationsQuery.data?.conversations || [];
  const totalCount = conversationsQuery.data?.total || 0;
  const overdueCount = overdueQuery.data?.count || 0;
  const contexts = contextsQuery.data?.contexts || [];

  // ==================== RENDER ====================

  return (
    <div className="p-6 space-y-6">
      {/* Header mit Statistiken */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{totalCount}</p>
                <p className="text-sm text-slate-500">Conversations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <AlertTriangle className={`w-5 h-5 ${overdueCount > 0 ? 'text-orange-500' : 'text-slate-400'}`} />
              <div>
                <p className={`text-2xl font-bold ${overdueCount > 0 ? 'text-orange-600' : ''}`}>{overdueCount}</p>
                <p className="text-sm text-slate-500">Überfällig</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold">
                  {conversations.filter(c => c.currentStatus !== 'closed').length}
                </p>
                <p className="text-sm text-slate-500">Aktiv</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter-Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Select value={contextFilter} onValueChange={setContextFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Kontext Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kontexte</SelectItem>
                {contexts.map(ctx => (
                  <SelectItem key={ctx} value={ctx}>{ctx}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant={overdueOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setOverdueOnly(!overdueOnly)}
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              Überfällig
            </Button>

            <div className="flex-1 flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suche nach Partner..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="max-w-xs"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversations-Tabelle */}
      <Card>
        <CardHeader>
          <CardTitle>Conversations ({totalCount})</CardTitle>
        </CardHeader>
        <CardContent>
          {conversationsQuery.isLoading ? (
            <div className="text-center py-8 text-slate-500">Lade Conversations...</div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Keine Conversations gefunden. Tracke deine erste Conversation über das LinkedIn Userscript.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>Kontext</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Letzte Aktion</TableHead>
                  <TableHead>Wiedervorlage</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conversations.map(conv => (
                  <TableRow
                    key={conv.id}
                    className={`cursor-pointer hover:bg-slate-50 ${isOverdue(conv) ? 'bg-orange-50' : ''}`}
                    onClick={() => openDetailDialog(conv)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <div>
                          <span className="font-medium">{conv.partnerName}</span>
                          {conv.profileId && (
                            <a
                              href={`/profile-manager/${conv.profileId}`}
                              className="ml-2 text-xs text-blue-500 hover:underline"
                              onClick={e => e.stopPropagation()}
                            >
                              Profil
                            </a>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">{conv.currentContext || '—'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[conv.currentStatus]}>
                        {STATUS_LABELS[conv.currentStatus]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(conv.updatedAt)}
                    </TableCell>
                    <TableCell>
                      {conv.nextFollowUp ? (
                        <span className={`text-sm font-medium ${isOverdue(conv) ? 'text-orange-600' : 'text-slate-600'}`}>
                          {isOverdue(conv) && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                          {formatDate(conv.nextFollowUp)}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openStatusDialog(conv)}
                          title="Status ändern"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openLogDialog(conv)}
                          title="Log-Eintrag"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(conv.threadUrl, '_blank')}
                          title="LinkedIn Thread öffnen"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ==================== DETAIL DIALOG ==================== */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              {selectedConversation?.partnerName}
            </DialogTitle>
            <DialogDescription>
              Conversation-Details und Log-Historie
            </DialogDescription>
          </DialogHeader>
          {selectedConversation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Status:</span>
                  <Badge className={`ml-2 ${STATUS_COLORS[selectedConversation.currentStatus]}`}>
                    {STATUS_LABELS[selectedConversation.currentStatus]}
                  </Badge>
                </div>
                <div>
                  <span className="text-slate-500">Kontext:</span>
                  <span className="ml-2">{selectedConversation.currentContext || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Wiedervorlage:</span>
                  <span className={`ml-2 ${isOverdue(selectedConversation) ? 'text-orange-600 font-medium' : ''}`}>
                    {formatDate(selectedConversation.nextFollowUp)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Erstellt:</span>
                  <span className="ml-2">{formatDate(selectedConversation.createdAt)}</span>
                </div>
              </div>

              {/* Log-Einträge */}
              {selectedConversation.logEntries && selectedConversation.logEntries.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2">Log-Historie</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedConversation.logEntries.map(entry => (
                      <div key={entry.id} className="flex items-start gap-3 text-sm border-l-2 border-slate-200 pl-3 py-1">
                        <span className="text-slate-400 whitespace-nowrap">{formatDate(entry.loggedAt)}</span>
                        <Badge className={`text-xs ${STATUS_COLORS[entry.action]}`}>
                          {STATUS_LABELS[entry.action]}
                        </Badge>
                        {entry.note && <span className="text-slate-600">{entry.note}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(selectedConversation.threadUrl, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  LinkedIn Thread
                </Button>
                {selectedConversation.profileId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/profile-manager/${selectedConversation.profileId}`, '_blank')}
                  >
                    <User className="w-4 h-4 mr-1" />
                    Profil öffnen
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== STATUS DIALOG ==================== */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Status ändern</DialogTitle>
            <DialogDescription>
              {selectedConversation?.partnerName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Neuer Status</Label>
              <Select value={newStatus} onValueChange={(v) => setNewStatus(v as ConversationStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notiz (optional)</Label>
              <Input
                value={statusNote}
                onChange={e => setStatusNote(e.target.value)}
                placeholder="Kurze Notiz..."
              />
            </div>
            <div>
              <Label>Wiedervorlage</Label>
              <Select value={statusFollowUp} onValueChange={setStatusFollowUp}>
                <SelectTrigger>
                  <SelectValue placeholder="Keine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Keine</SelectItem>
                  <SelectItem value="1">+1 Tag</SelectItem>
                  <SelectItem value="3">+3 Tage</SelectItem>
                  <SelectItem value="7">+1 Woche</SelectItem>
                  <SelectItem value="14">+2 Wochen</SelectItem>
                  <SelectItem value="30">+1 Monat</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleStatusUpdate} disabled={updateStatusMutation.isPending}>
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== LOG DIALOG ==================== */}
      <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log-Eintrag hinzufügen</DialogTitle>
            <DialogDescription>
              {selectedConversation?.partnerName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Aktion</Label>
              <Select value={logAction} onValueChange={(v) => setLogAction(v as ConversationStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notiz (optional)</Label>
              <Input
                value={logNote}
                onChange={e => setLogNote(e.target.value)}
                placeholder="Kurze Notiz..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleAddLog} disabled={addLogMutation.isPending}>
              Hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
